import { SECTIONS } from '@alatzidis/core'
import { ARTIFACTS_BY_SECTION } from '../artifacts'

/**
 * What <head> says about a route.
 *
 * Derived from the same registries the section pages and the home grid read
 * (`SECTIONS`, `ARTIFACTS_BY_SECTION`), so a new artifact gets a title,
 * description and canonical the moment its card exists. Anything the
 * registries do not know about is `noindex`: unknown URLs, redirects and the
 * render-window sub-routes.
 */

export const SITE_URL = 'https://alatzidis.com'
export const SITE_NAME = 'alatzidis'

export const HOME_TITLE = 'alatzidis · play — nothing here was downloaded'
export const HOME_DESCRIPTION =
  'Nothing here was downloaded. First principles, played with — sound from oscillators, games from rules, light from rays, chess from stubbornness. A playground by Christos Alatzidis.'

export interface HeadSpec {
  title: string
  description: string
  /** Absolute canonical URL; omitted for pages that must not be indexed. */
  canonical?: string
  index: boolean
  ogType: 'website' | 'article'
  /** Absolute URL of the share card (one per section, drawn at build). */
  image?: string
  /** schema.org JSON-LD nodes for this route (step 6). */
  ld?: object[]
  /** Artifact pages: the heading a page must have if it draws none itself (step 7). */
  h1?: string
}

const AUTHOR = { '@type': 'Person', name: 'Christos Alatzidis' }

function breadcrumb(items: [string, string][]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map(([name, href], i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name,
      item: SITE_URL + href,
    })),
  }
}

const card = (id: string) => `${SITE_URL}/og/${id}.png`

/** First sentence, trimmed to something a search result can show. */
export function firstSentence(text: string, max = 170): string {
  const s = text.replace(/\s+/g, ' ').trim()
  const m = s.match(/^.+?[.!?](?=\s|$)/)
  let out = m ? m[0] : s
  if (out.length > max) out = out.slice(0, max - 1).replace(/\s+\S*$/, '') + '…'
  return out
}

const norm = (p: string) => (p.length > 1 ? p.replace(/\/+$/, '') : p) || '/'

export function headFor(pathname: string): HeadSpec {
  const path = norm(pathname)

  if (path === '/') {
    return {
      title: HOME_TITLE,
      description: HOME_DESCRIPTION,
      canonical: SITE_URL + '/',
      index: true,
      ogType: 'website',
      image: card('home'),
      ld: [
        {
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: SITE_NAME,
          alternateName: 'alatzidis · play',
          url: SITE_URL + '/',
          description: HOME_DESCRIPTION,
          inLanguage: 'en',
          author: AUTHOR,
        },
      ],
    }
  }

  for (const section of SECTIONS) {
    if (path === section.href) {
      const n = ARTIFACTS_BY_SECTION[section.id].filter((a) => a.status !== 'soon').length
      return {
        title: `${section.label} · ${SITE_NAME}`,
        description: `${section.blurb} ${n} ${n === 1 ? 'piece' : 'pieces'}, all running in your browser.`,
        canonical: SITE_URL + section.href,
        index: true,
        ogType: 'website',
        image: card(section.id),
        ld: [
          {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: section.label,
            url: SITE_URL + section.href,
            description: section.blurb,
            isPartOf: { '@type': 'WebSite', name: SITE_NAME, url: SITE_URL + '/' },
          },
          breadcrumb([[SITE_NAME, '/'], [section.label, section.href]]),
        ],
      }
    }
    for (const a of ARTIFACTS_BY_SECTION[section.id]) {
      if (a.status !== 'soon' && path === a.href) {
        return {
          title: `${a.title} · ${section.label.toLowerCase()} · ${SITE_NAME}`,
          description: firstSentence(a.blurb),
          canonical: SITE_URL + a.href,
          index: true,
          ogType: 'website',
          image: card(section.id),
          h1: a.title,
          ld: [
            {
              '@context': 'https://schema.org',
              '@type': 'CreativeWork',
              name: a.title,
              url: SITE_URL + a.href,
              description: firstSentence(a.blurb, 300),
              genre: section.label,
              inLanguage: 'en',
              isAccessibleForFree: true,
              author: AUTHOR,
              isPartOf: { '@type': 'WebSite', name: SITE_NAME, url: SITE_URL + '/' },
            },
            breadcrumb([[SITE_NAME, '/'], [section.label, section.href], [a.title, a.href]]),
          ],
        }
      }
    }
  }

  return {
    title: `Nothing here · ${SITE_NAME}`,
    description: HOME_DESCRIPTION,
    index: false,
    ogType: 'website',
  }
}

/**
 * Every path that `headFor` marks as indexable, in registry order. The build
 * turns this into sitemap.xml, so a new artifact card is in the sitemap
 * without a manual edit.
 */
export function indexablePaths(): string[] {
  const out = ['/']
  for (const section of SECTIONS) {
    out.push(section.href)
    for (const a of ARTIFACTS_BY_SECTION[section.id]) {
      if (a.status !== 'soon') out.push(a.href)
    }
  }
  return [...new Set(out)].filter((p) => headFor(p).index)
}

function setMeta(attr: 'name' | 'property', key: string, content: string | null) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`)
  if (content === null) {
    el?.remove()
    return
  }
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function setCanonical(href: string | null) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
  if (href === null) {
    el?.remove()
    return
  }
  if (!el) {
    el = document.createElement('link')
    el.rel = 'canonical'
    document.head.appendChild(el)
  }
  el.href = href
}

function setLd(nodes: object[] | undefined) {
  document.head.querySelectorAll('script[data-ld]').forEach((el) => el.remove())
  for (const n of nodes ?? []) {
    const el = document.createElement('script')
    el.type = 'application/ld+json'
    el.setAttribute('data-ld', '')
    el.textContent = JSON.stringify(n)
    document.head.appendChild(el)
  }
}

/** Write a spec into document.head (idempotent). */
export function applyHead(spec: HeadSpec) {
  setLd(spec.ld)
  document.title = spec.title
  setMeta('name', 'description', spec.description)
  setMeta('name', 'robots', spec.index ? null : 'noindex, follow')
  setCanonical(spec.canonical ?? null)
  const url = spec.canonical ?? null
  setMeta('property', 'og:site_name', SITE_NAME)
  setMeta('property', 'og:type', spec.ogType)
  setMeta('property', 'og:title', spec.title)
  setMeta('property', 'og:description', spec.description)
  setMeta('property', 'og:url', url)
  setMeta('property', 'og:image', spec.image ?? null)
  setMeta('property', 'og:image:width', spec.image ? '1200' : null)
  setMeta('property', 'og:image:height', spec.image ? '630' : null)
  setMeta('name', 'twitter:card', spec.image ? 'summary_large_image' : 'summary')
  setMeta('name', 'twitter:title', spec.title)
  setMeta('name', 'twitter:description', spec.description)
  setMeta('name', 'twitter:image', spec.image ?? null)
}

const esc = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

/** <head> markup for a spec — the build-time twin of `applyHead`. */
export function headHtml(spec: HeadSpec): string {
  const m = (a: string, k: string, v: string) => `<meta ${a}="${k}" content="${esc(v)}" />`
  const out = [
    `<title>${esc(spec.title)}</title>`,
    m('name', 'description', spec.description),
    m('property', 'og:site_name', SITE_NAME),
    m('property', 'og:type', spec.ogType),
    m('property', 'og:title', spec.title),
    m('property', 'og:description', spec.description),
    m('name', 'twitter:card', spec.image ? 'summary_large_image' : 'summary'),
    m('name', 'twitter:title', spec.title),
    m('name', 'twitter:description', spec.description),
  ]
  if (spec.canonical) {
    out.push(`<link rel="canonical" href="${esc(spec.canonical)}" />`, m('property', 'og:url', spec.canonical))
  }
  if (spec.image)
    out.push(
      m('property', 'og:image', spec.image),
      m('property', 'og:image:width', '1200'),
      m('property', 'og:image:height', '630'),
      m('name', 'twitter:image', spec.image),
    )
  if (!spec.index) out.push(m('name', 'robots', 'noindex, follow'))
  for (const n of spec.ld ?? []) {
    // '<' escaped so no string can close the script element.
    out.push(`<script type="application/ld+json" data-ld>${JSON.stringify(n).replace(/</g, '\\u003c')}</script>`)
  }
  return out.join('\n    ')
}

/**
 * Crawler-readable body for a route: one h1, the description, and plain links
 * to what lives below it. React replaces this on mount; it exists so the text
 * is in the HTML before any JS runs.
 */
export function bodyHtml(pathname: string): string {
  const path = norm(pathname)
  const a = (href: string, text: string) => `<li><a href="${esc(href)}">${esc(text)}</a></li>`
  if (path === '/') {
    const items = SECTIONS.map((s) => a(s.href, `${s.label} — ${s.blurb}`)).join('')
    return `<main><h1>alatzidis</h1><p>${esc(HOME_DESCRIPTION)}</p><ul>${items}</ul></main>`
  }
  for (const section of SECTIONS) {
    if (path === section.href) {
      const items = ARTIFACTS_BY_SECTION[section.id]
        .filter((x) => x.status !== 'soon')
        .map((x) => a(x.href, `${x.title} — ${firstSentence(x.blurb)}`))
        .join('')
      return `<main><h1>${esc(section.label)}</h1><p>${esc(section.blurb)}</p><ul>${items}</ul><p><a href="/">alatzidis</a></p></main>`
    }
    for (const x of ARTIFACTS_BY_SECTION[section.id]) {
      if (x.status !== 'soon' && path === x.href) {
        return `<main><h1>${esc(x.title)}</h1><p>${esc(x.blurb)}</p><p><a href="${esc(section.href)}">${esc(section.label)}</a> · <a href="/">alatzidis</a></p></main>`
      }
    }
  }
  return ''
}
